// ─── What: domains and scales ─────────────────────────────────────────────────
//
// Everything between "we have raw series" and "we can draw". Pure: no d3
// selections, no DOM. The subtle part is computeProbDomain, which has to mirror
// what the probability panel actually draws — see the note there.

import * as d3 from 'd3';
import type { OHLCVPoint } from '../../../api/types';
import type { MarketSeriesData } from '../../../api/marketPricesClient';
import { flipOutcomeSeries } from '../chartCandles';
import { fitProbabilityDomain, type AxisFit } from '../chartYDomain';
import { MARGIN, Y_EDGE_PAD } from './layout';
import type {
    CandleDataFor,
    ExtendedSeries,
    ProbDomain,
} from './drawContext';
import type { EffectiveChartType } from './constants';

// ─── "Now" ────────────────────────────────────────────────────────────────────

const NOW_QUANTUM_MS = 60_000;

/**
 * A raw Date() moves every render, so the right bound would creep and a window
 * pinned to the live edge could never compare equal to itself — refetching on
 * every return to "today". A minute is coarse enough to absorb an interaction,
 * fine enough that the live edge stays visually current at any resolution.
 */
export function quantizedNow(): Date {
    return new Date(Math.floor(Date.now() / NOW_QUANTUM_MS) * NOW_QUANTUM_MS);
}

// ─── Series extension ─────────────────────────────────────────────────────────

/**
 * Trim leading zero-volume candles so each line starts at its first real trade,
 * then carry the last point forward so the line reaches the right edge.
 */
export function extendSeriesToNow(
    seriesWithData: MarketSeriesData[],
    eventEndDate: Date | null | undefined,
    now: Date
): ExtendedSeries[] {
    return seriesWithData.map(s => {
        const firstLiveIdx = s.data.findIndex(d => d.vol > 0);
        const trimmedData =
            firstLiveIdx > 0 ? s.data.slice(firstLiveIdx) : s.data;
        const last = trimmedData[trimmedData.length - 1];
        const endDate = eventEndDate ? new Date(eventEndDate) : null;
        const extendTo = endDate && endDate < now ? endDate : now;
        const extended =
            last && last.t < extendTo
                ? [...trimmedData, { ...last, t: extendTo, vol: 0 }]
                : trimmedData;
        return {
            ...s,
            data: extended,
            firstTradeDate: trimmedData[0]?.t ?? s.data[0]?.t,
        };
    });
}

// ─── X ────────────────────────────────────────────────────────────────────────

export function computeFullDomain(series: ExtendedSeries[]): [Date, Date] {
    const allDates = series.flatMap(s => s.data.map(d => d.t));
    return d3.extent(allDates) as [Date, Date];
}

export function makeXScale(
    xDomain: [Date, Date],
    w: number
): d3.ScaleTime<number, number> {
    return d3
        .scaleTime()
        .domain(xDomain)
        .range([MARGIN.left, MARGIN.left + w]);
}

/**
 * Zoom/pan bounds: the full event range, not just the loaded window, so the user
 * can pan beyond the current data and trigger a windowed refetch. `now` is
 * floored to the minute and can land just behind the newest candle, so never let
 * that put the freshest data outside the bounds.
 */
export function computePanBounds(
    eventStartDate: Date | null | undefined,
    eventEndDate: Date | null | undefined,
    now: Date,
    fullDomain: [Date, Date]
): [Date, Date] {
    const boundsEnd =
        eventEndDate && eventEndDate < now
            ? eventEndDate
            : new Date(Math.max(now.getTime(), fullDomain[1].getTime()));
    return eventStartDate && eventEndDate
        ? [eventStartDate, boundsEnd]
        : fullDomain;
}

// ─── Candle side ──────────────────────────────────────────────────────────────

/**
 * Candlesticks may show either side. A real NO fetch is preferred — mirroring
 * cannot show a bucket where only the NO side traded — but mirroring is the
 * fallback so the toggle never blanks the chart.
 */
export function makeCandleDataFor(
    candleSide: 'yes' | 'no',
    noByMarket: ReadonlyMap<string, OHLCVPoint[]>
): CandleDataFor {
    return sd => {
        if (candleSide !== 'no') return sd.data;
        const fetched = noByMarket.get(sd.conditionId);
        if (fetched && fetched.length > 0) return fetched;
        return flipOutcomeSeries(sd.data);
    };
}

// ─── Y (probability) ──────────────────────────────────────────────────────────

export interface ProbDomainInput {
    series: ExtendedSeries[];
    chartType: EffectiveChartType;
    /** The axis-fit setting, before the stacked override. */
    fit: AxisFit;
    selectedIds: string[];
    showNoLine?: string | null;
    xDomain: [Date, Date];
    candleDataFor: CandleDataFor;
}

/**
 * Fit the axis to what is ACTUALLY DRAWN, not to what exists.
 *
 * Candles replace the line and skip the NO mirror entirely, so folding NO in
 * there would hold the axis open for a line that is not on screen. Stacked
 * shares are a 100% composition and never fit to the data. Pinned modes discard
 * the extent, so don't walk every point per render.
 */
export function computeProbDomain(input: ProbDomainInput): ProbDomain {
    const { series, chartType, fit, selectedIds, showNoLine, xDomain } = input;
    const probFit: AxisFit = chartType === 'stacked' ? 'full' : fit;

    const visibleProbValues: number[] = [];
    for (const s of probFit === 'auto' ? series : []) {
        const drawsCandles =
            chartType === 'candles' && selectedIds.includes(s.conditionId);
        const drawsNoLine = !drawsCandles && showNoLine === s.conditionId;
        for (const d of drawsCandles ? input.candleDataFor(s) : s.data) {
            if (d.t < xDomain[0] || d.t > xDomain[1]) continue;
            if (drawsCandles) {
                visibleProbValues.push(d.low, d.high);
            } else {
                visibleProbValues.push(d.close);
            }
            if (drawsNoLine) {
                visibleProbValues.push(1 - d.close);
            }
        }
    }
    return fitProbabilityDomain(visibleProbValues, probFit);
}

export function makeYScale(
    probDomain: ProbDomain,
    probHeight: number
): d3.ScaleLinear<number, number> {
    return d3
        .scaleLinear()
        .domain(probDomain.domain)
        .range([probHeight - 4 - Y_EDGE_PAD, MARGIN.top + Y_EDGE_PAD]);
}
